/**
 * Pickup times as Xobo stores them: Nairobi wall-clock digits.
 *
 * orders keeps scheduled_time as "YYYY-MM-DD HH:mm:ss" read in
 * Africa/Nairobi, whatever zone the browser is in. It comes back either in
 * that form (Redis) or as an ISO string ending in Z (the SQL rebuild
 * serialises the same digits with a Z). Both mean the same Nairobi wall
 * clock, so the digits are read as Nairobi time and the Z is ignored.
 * Reading the Z as UTC would move the time by 3 hours.
 *
 * The date picker works on the same digits (value-format), so a time picked
 * is never converted through the browser's zone and never compared as a
 * Date against a string.
 *
 * Pure: every function that needs "now" takes it as an argument.
 */
import { DateTime } from "luxon";

// The installed @types/luxon exposes DateTime as a value only, so the type
// is taken from what it returns.
type DT = ReturnType<typeof DateTime.now>;

export const XOBO_ZONE = "Africa/Nairobi";
/** The API's format, in luxon tokens. */
export const WALL_CLOCK_FORMAT = "yyyy-MM-dd HH:mm:ss";
/** The same format in Element Plus (dayjs) tokens, for value-format. */
export const PICKER_VALUE_FORMAT = "YYYY-MM-DD HH:mm:ss";
/** How the picker shows the chosen time. */
export const PICKER_DISPLAY_FORMAT = "DD MMM YYYY, HH:mm";

export const SCHEDULE_MIN_LEAD_MINUTES = 15;
export const SCHEDULE_MAX_DAYS = 30;
/**
 * Dedicated bookings: 2 hours to 30 days ahead, so ops have time to assign
 * and phone a driver. orders refuses anything else (dedicated_lead_time).
 */
export const DEDICATED_MIN_LEAD_MINUTES = 120;

export type PickupMode = "asap" | "scheduled";

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/** Now, in Nairobi. Takes a clock for tests. */
export function nairobiNow(now: DT | Date | number = Date.now()): DT {
  const dt = DateTime.isDateTime(now)
    ? now
    : now instanceof Date
      ? DateTime.fromJSDate(now)
      : DateTime.fromMillis(now);
  return dt.setZone(XOBO_ZONE);
}

/** A moment as the API's Nairobi wall-clock string. */
export function toWallClock(dt: DT): string {
  return dt.setZone(XOBO_ZONE).toFormat(WALL_CLOCK_FORMAT);
}

/**
 * Read stored or picked digits as Nairobi wall clock. Accepts
 * "YYYY-MM-DD HH:mm[:ss]" and ISO forms with or without a Z or offset (the
 * offset is ignored, see above). Anything else gives null.
 */
export function parseWallClock(value: unknown): DT | null {
  if (typeof value !== "string") return null;
  const m = WALL_CLOCK_RE.exec(value.trim());
  if (!m) return null;
  const dt = DateTime.fromObject(
    {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: Number(m[4]),
      minute: Number(m[5]),
      second: m[6] ? Number(m[6]) : 0,
    },
    { zone: XOBO_ZONE },
  );
  return dt.isValid ? dt : null;
}

/**
 * disabled-date for the picker. A calendar cell is a date, not a moment: its
 * year, month and day are compared with today's date in Nairobi, so today
 * stays selectable (the old check compared with new Date() and disabled it).
 * Days after the 30-day limit are disabled too.
 */
export function isDisabledPickupDay(
  cell: Date,
  now: DT | Date | number = Date.now(),
  minLeadMinutes: number = SCHEDULE_MIN_LEAD_MINUTES,
): boolean {
  const day = DateTime.fromObject(
    { year: cell.getFullYear(), month: cell.getMonth() + 1, day: cell.getDate() },
    { zone: XOBO_ZONE },
  );
  // Dedicated's 2 hours can run past midnight (a booking after 22:00), and
  // then today has no allowed time. On-Demand keeps today as before.
  const lead = minLeadMinutes > SCHEDULE_MIN_LEAD_MINUTES ? minLeadMinutes : 0;
  const today = nairobiNow(now).plus({ minutes: lead }).startOf("day");
  const lastDay = nairobiNow(now).plus({ days: SCHEDULE_MAX_DAYS }).startOf("day");
  return day < today || day > lastDay;
}

export type ScheduleError = "required" | "too_soon" | "too_far";

/** Why a scheduled pickup time cannot be used, or null when it can. */
export function scheduleError(
  value: unknown,
  now: DT | Date | number = Date.now(),
  minLeadMinutes: number = SCHEDULE_MIN_LEAD_MINUTES,
): ScheduleError | null {
  const at = parseWallClock(value);
  if (!at) return "required";
  const n = nairobiNow(now);
  if (at < n.plus({ minutes: minLeadMinutes })) return "too_soon";
  if (at > n.plus({ days: SCHEDULE_MAX_DAYS })) return "too_far";
  return null;
}

export function scheduleErrorMessage(code: ScheduleError, minLeadMinutes: number = SCHEDULE_MIN_LEAD_MINUTES): string {
  switch (code) {
    case "too_soon":
      return `Choose a time at least ${leadText(minLeadMinutes)} from now`;
    case "too_far":
      return `Choose a time within the next ${SCHEDULE_MAX_DAYS} days`;
    default:
      return "Choose a pickup date and time";
  }
}

/** "15 minutes", "2 hours". */
function leadText(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  return `${minutes} minutes`;
}

/**
 * The scheduled_time createOrder receives. orders still requires the field
 * in its strict format: for ASAP it is now (orders stores its own now), and
 * for a schedule it is the picked time in canonical form.
 */
export function resolveScheduledTime(
  mode: PickupMode,
  value: unknown,
  now: DT | Date | number = Date.now(),
): string {
  if (mode === "scheduled") {
    const at = parseWallClock(value);
    if (at) return toWallClock(at);
  }
  return toWallClock(nairobiNow(now));
}

/** "04 Nov 2026, 08:00": the app's date style, in Nairobi time. "" if unreadable. */
export function formatWallClock(value: unknown): string {
  const at = parseWallClock(value);
  return at ? at.toFormat("dd MMM yyyy, HH:mm") : "";
}

/** "Tue 4 Nov, 08:00": a booked pickup, in Nairobi time. "" if unreadable. */
export function formatBookedFor(value: unknown): string {
  const at = parseWallClock(value);
  return at ? at.setLocale("en-GB").toFormat("ccc d LLL, HH:mm") : "";
}
